import { Component, OnInit, OnChanges, SimpleChanges, Input, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ListenService, BodensteckbriefEntry, BodensteckbriefSpalte, BodensteckbriefFbfRow } from './listen.service';

/** Eine Header-Gruppe (eine TH-Zelle) in einer Header-Zeile der Schraffurtabelle.
 *  cols = die Spalten-IDs die diese TH-Zelle ueberspannt (colspan). */
interface HeaderGroup {
  label: string;
  cols: { id: string; label: string }[];
  /** Optional > 1, wenn die Top-Gruppe ueber leere Sub-Header rowspannt. */
  rowspan?: number;
  /** Schreibrichtung des Labels: 'h' = horizontal, 'v' = vertikal (rotiert).
   *  Wird gruppen-einheitlich vergeben (siehe assignOrientations): alle
   *  Geschwister mit demselben Elternteil teilen dieselbe Orientierung. */
  orientation?: 'h' | 'v';
  /** Letzter Fallback: verkleinerte Schriftgroesse in px, wenn das Label trotz
   *  Orientierung + Umbruch nicht in die Zelle passt. undefined = Basis-Groesse. */
  fontPx?: number;
  /** True, wenn diese Header-Zelle ein BLATT ist (tiefstes Level - erreicht die
   *  unterste Header-Zeile, kein Unter-Header darunter) und damit als Sortier-
   *  Spalte angeklickt werden darf. Wird in markSortableHeaders gesetzt. */
  sortable?: boolean;
  /** Die Schraffur-Spalten-IDs, nach denen ein Klick auf dieses Blatt sortiert
   *  (eine colspan-Blattzelle deckt ggf. mehrere Spalten ab). */
  sortCols?: string[];
}

/** Spalten-Tokens, die NICHT zu Schraffur-Spalten gehoeren - das sind die
 *  fixen Tabellen-Spalten (Lokalname, Abk., NKS, 5tel, Forstbetrieb), die in
 *  der Print-Tabelle eigene Bereiche bekommen, nicht den Schraffur-Bereich. */
const NON_SCHRAF_TOKENS = [
  'lokalname', 'abkürzung', 'abkuerzung',
  'nks', 'nährkraftstufe', 'naehrkraftstufe',
  'fünftelstufe', 'fuenftelstufe', '5tel',
  'forstbetrieb', 'taufprofil',
];

/** Breite einer Schraffur-Spalte in px (muss zu `.col-schraf` im CSS passen).
 *  Eine Header-Gruppe ueberspannt nCols solcher Spalten, hat also
 *  `nCols * SCHRAF_COL_W` px horizontal zur Verfuegung. Voraussetzung dafuer,
 *  dass diese Breite real greift: `.pv-table` braucht `width:100%` (sonst
 *  ignoriert der Browser `table-layout:fixed` + colgroup -> auto-Layout). */
const SCHRAF_COL_W = 18;
/** Max. Laenge einer vertikalen Header-Zeile in px (muss zur `height` von
 *  `.vh > span` im CSS passen). Begrenzt, damit lange vertikale Labels in mehr,
 *  kuerzere Zeilen brechen und die Breite nutzen. */
const VERT_LINE_CAP = 64;
/** Basis-Schriftgroessen der Header (px) - muessen zum CSS passen. */
const HEADER_FONT_LEAF = 10;
const HEADER_FONT_GROUP = 11;
/** Untergrenze fuer den Schrift-Verkleinerungs-Fallback (px). */
const MIN_HEADER_FONT = 7;

/** REGEL: ALLE Daten einer HBF werden in der Preview dargestellt. Diese Menge
 *  listet die Eintrags-Schluessel, die die Preview BEREITS strukturiert anzeigt,
 *  bewusst als Metadaten ignoriert, ODER ueber buildEffectiveSections in
 *  `sections` faltet. Jeder ANDERE Schluessel mit Inhalt wird generisch im
 *  Catch-all-Block angezeigt (collectUnknownData) - so kann kein Feld stumm
 *  verloren gehen, auch kuenftige Parser-Felder nicht. */
const KNOWN_ENTRY_KEYS = new Set<string>([
  // Metadaten - nicht anzuzeigen
  'id', 'pdf', 'page', '_provenance',
  // strukturiert dargestellt
  'hauptbodenform', 'spalten', 'schraf_col_order', 'feinbodenformen',
  'sections', 'horizont_diagramme', 'horizont',
  'substrat_layers', 'substrat_kuerzel', 'substrat', 'horizont_diagramm',
  'fussnoten', 'klassifikation', 'seitenzahl', 'fbf_klassensuffix', 'raw_fills',
  // Legacy-Hinweisfelder, die buildEffectiveSections in `sections` faltet
  'humushinweise', 'humushinweise_lines', 'hydromorphiehinweise', 'extra_left_sections',
]);
/** Padding/Border-Reserve je Header-Zelle (px). Etwas grosszuegiger als das
 *  reine CSS-Padding, damit kein Wort horizontal "gerade so" als passend gilt
 *  und dann doch abgeschnitten wird - im Zweifel lieber vertikal (Anwender-
 *  Regel: passt es horizontal nicht, dann vertikal; NIE mitten im Wort
 *  umbrechen). 6px = vertikal ab ~83% Fuellung, kurze 2-Zeichen-Labels bleiben
 *  in 18px-Spalten aber horizontal. */
const HEADER_CELL_PAD = 6;

/** Sortier-Reihenfolge der Naehrkraftstufe (NKS) im bF-Katalog. Bewusst NICHT
 *  alphabetisch (A,K,M,R,Z), sondern fachlich von naehrstoffreich nach -arm:
 *  R(eich) - K(raeftig) - M(ittel) - Z(iemlich arm) - A(rm). Vom Anwender so
 *  gewuenscht. Unbekannte Werte landen hinter den bekannten, leere ganz hinten. */
const NKS_ORDER = ['R', 'K', 'M', 'Z', 'A'];

@Component({
  standalone: false,
  selector: 'app-bodensteckbrief-preview',
  templateUrl: './bodensteckbrief-preview.component.html',
  styleUrls: ['./bodensteckbrief-preview.component.css'],
  // Nur im Standalone-Modus (Route, nicht eingebettet) bekommt der Host das
  // Vollhöhen-Scroll-Layout. Eingebettet (Reader/Editor-Live-Preview) bleibt er
  // im normalen Fluss, damit der umgebende Scroll-Container greift.
  host: { '[class.pv-standalone]': '!embedded' },
})
export class BodensteckbriefPreviewComponent implements OnInit, OnChanges {
  private listenService = inject(ListenService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  /** Eingebetteter Modus (z.B. Live-Preview im Editor): Parent reicht den
   *  Eintrag direkt rein, dann werden Route-/Liste-Lookup uebersprungen.
   *  Reference-Wechsel triggert ngOnChanges -> rebuild der View-Daten. */
  @Input() entry: BodensteckbriefEntry | null = null;
  /** Im eingebetteten Modus: Toolbar und Container-Padding weglassen. */
  @Input() embedded = false;

  notFound = false;
  loading = true;
  /** Route-ID des angezeigten Steckbriefs (nur Standalone-Route-Modus) - wird
   *  beim Zurueck mitgegeben, damit die Liste wieder darauf zentriert. */
  private entryId = '';
  /** Original-PDF-Seite seitlich dazu blenden (Admin-On-Demand). */
  showOriginal = false;

  /** Lokale Sortierung der FBF-Zeilen. '' = Original-Reihenfolge aus dem
   *  Steckbrief (so wie der Druck-Eintrag im SEA95 erscheint). Klick auf
   *  einen Header in der Tabelle setzt das Feld + toggled die Richtung.
   *  Schraffuren wandern automatisch mit, weil die ganze FBF-Zeile als
   *  Objekt sortiert wird. */
  sortField: '' | 'lokalname' | 'abkuerzung' | 'nahrkraftstufe' | 'funftelstufe' | 'forstbetrieb' | 'schraf' = '';
  sortDir: 'asc' | 'desc' = 'asc';
  /** Aktive Schraffur-Sortier-Spalten (nur wenn sortField === 'schraf'). Eine
   *  Blatt-Header-Zelle kann per colspan mehrere physische Spalten abdecken. */
  sortSchrafCols: string[] = [];

  /** Vorberechnete View-Daten (einmal pro Entry-Load). */
  schrafCols: string[] = [];
  headerRows: HeaderGroup[][] = [];
  headerMaxLevel = 1;
  hasFootnoteCol = false;
  /** "Humus" / "Eisen" / "Hydromorphie" / sonstige Hinweis-Sections. */
  humusItems: Array<{ key: string; value: string }> = [];
  humusLines: string[] = [];
  hydromorphieLines: string[] = [];
  extraLeftSections: Array<{ title: string; kind: string; lines: string[]; items: Array<{ key: string; value: string }> }> = [];
  /** Catch-all: alle Eintrags-Felder, die sonst nirgends dargestellt werden
   *  (nicht in KNOWN_ENTRY_KEYS). Erzwingt die Regel "ALLE Daten anzeigen". */
  extraDataBlocks: Array<{ title: string; lines: string[]; items: Array<{ key: string; value: string }> }> = [];
  klassifikation = '';
  seitenzahl = '';

  /** Lazy-Canvas fuer echte Text-Breitenmessung (Header-Orientierung).
   *  undefined = noch nicht versucht · null = kein Canvas (z.B. Unit-Test) ->
   *  dann greift die Zeichen-Schaetzung in measureLabelWidth. */
  private _measureCtx?: CanvasRenderingContext2D | null;
  private _measureFamily = 'sans-serif';

  async ngOnInit(): Promise<void> {
    // Eingebettet (Editor-Live-Preview): entry kommt per @Input, kein Route-Lookup.
    if (this.entry) {
      this.notFound = false;
      this.loading = false;
      this.buildViewData(this.entry);
      return;
    }
    const id = this.route.snapshot.paramMap.get('id') || '';
    this.entryId = id;
    const list = (await this.listenService.getList('Bodensteckbriefe')) as BodensteckbriefEntry[] | null;
    const found = (list || []).find(e => e?.id === id) || null;
    this.entry = found;
    this.notFound = !found;
    this.loading = false;
    if (found) this.buildViewData(found);
  }

  /** Bei eingebettetem Modus aendert sich `entry` mit jeder Editor-Mutation
   *  (per shallow-Clone-Trick im Editor) -> View-Daten neu berechnen. Das
   *  Leeren der Akkumulator-Listen passiert in buildViewData selbst, damit
   *  auch ngOnInit (das ebenfalls buildViewData ruft) sauber startet -
   *  sonst doppelter Eintrag beim ersten Render. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entry'] && this.entry) {
      this.loading = false;
      this.notFound = false;
      this.buildViewData(this.entry);
    }
  }

  back(): void {
    // Mit list/entry zurueck, damit die Liste wieder geoeffnet ist und auf den
    // gerade angesehenen Steckbrief zentriert (Vorschau ist nur im Route-Modus).
    this.router.navigate(['/listenverwaltung'], {
      queryParams: { list: 'Bodensteckbriefe', entry: this.entryId || null },
    });
  }

  toggleOriginal(): void { this.showOriginal = !this.showOriginal; }

  // ========== Derived view data ==========

  private buildViewData(e: BodensteckbriefEntry): void {
    // Akkumulator-Listen IMMER zuerst leeren - sonst sammeln sich Eintraege
    // bei jedem Re-Build (ngOnInit + ngOnChanges koennen beim Erstrender
    // zusammen feuern und ohne Reset werden Sektionen doppelt erfasst).
    this.humusItems = [];
    this.humusLines = [];
    this.hydromorphieLines = [];
    this.extraLeftSections = [];
    this.extraDataBlocks = [];
    const rawFills: Record<string, Record<string, string>> = e.raw_fills || {};
    // Explizite Reihenfolge aus dem Editor (move/insert/delete) hat Vorrang;
    // sonst Heuristik wie bisher.
    if (Array.isArray(e.schraf_col_order) && e.schraf_col_order.length) {
      this.schrafCols = e.schraf_col_order.filter(c => !!(e.spalten || {})[c]);
    } else {
      this.schrafCols = this.computeSchrafCols(e.spalten || {}, rawFills, e.feinbodenformen || []);
    }
    const rawLvls: Record<string, string[]> = {};
    for (const c of this.schrafCols) rawLvls[c] = e.spalten?.[c]?.ebenen || [];
    this.headerMaxLevel = Math.max(1, ...Object.values(rawLvls).map(a => a.length));
    // Verstuemmelte Spalten (Blatt-Wert nicht in der untersten Ebene, weil der
    // D_Parser den Kopf auf wenige Ebenen zusammenklebte) unten ausrichten -
    // sonst floatet das Blatt nach oben und darunter bleiben leere weisse Zellen
    // (Tieflehm-Fahlerde Spalte 16). Reine Anzeige-Korrektur, mutiert den
    // Eintrag NICHT.
    const lvls: Record<string, string[]> = {};
    for (const c of this.schrafCols) lvls[c] = this.bottomAlignLeaf(rawLvls[c], this.headerMaxLevel);
    this.headerRows = this.computeHeaderRows(this.schrafCols, lvls, this.headerMaxLevel);
    // Leere Header-Zellen vertikal (und dann horizontal) verbinden, damit im
    // leeren Zwischenraum ueber den Blaettern keine Trennlinien stehen - eine
    // durchgehende Leerzelle pro Spalte statt mehrerer gestapelter.
    this.headerRows = this.mergeEmptyCells(this.headerRows, this.schrafCols);
    this.assignOrientations(this.headerRows);
    this.fitFontSizes(this.headerRows);
    // Blatt-Header (tiefstes Level) als sortierbar markieren, damit der Anwender
    // die Schraffur-Tabelle auch nach einer einzelnen Schraffur-Spalte sortieren
    // kann (markierte Zeilen zuerst). Gruppen-Header bleiben bewusst unklickbar.
    this.markSortableHeaders(this.headerRows, this.headerMaxLevel);
    this.hasFootnoteCol = (e.feinbodenformen || []).some(f => !!f.footnote_ref);
    // WICHTIG: nicht nur e.sections, sondern auch die Legacy-Top-Level-Felder
    // aus dem D_Parser-Rohimport (humushinweise, humushinweise_lines,
    // extra_left_sections [Eisen/Baender/sonstige], hydromorphiehinweise). Der
    // Editor migriert diese beim Speichern in e.sections; Eintraege, die noch
    // NICHT durch den Editor liefen, haetten in der Preview sonst fehlende
    // Hinweis-Bloecke (Humus/Eisen/Baender). buildEffectiveSections vereint
    // beide Quellen read-only (dedupliziert nach Titel).
    this.classifySections(this.buildEffectiveSections(e));
    // Auffangnetz: jedes restliche Inhalts-Feld wird generisch angezeigt.
    this.extraDataBlocks = this.collectUnknownData(e);
    this.klassifikation = e.klassifikation || '';
    this.seitenzahl = e.seitenzahl || '';
  }

  /** Filter: alles raus was kein "Schraffurfeld" ist. Logik analog zu
   *  D_Parser print_view (app.py _is_skipcol). */
  private computeSchrafCols(
    spalten: Record<string, BodensteckbriefSpalte>,
    rawFills: Record<string, Record<string, string>>,
    fbfs: BodensteckbriefFbfRow[],
  ): string[] {
    const sortedIds = Object.keys(spalten)
      .filter(c => /^-?\d+$/.test(c))
      .sort((a, b) => Number(a) - Number(b));

    const isNonSchrafHeader = (sp: BodensteckbriefSpalte): boolean => {
      const e = (sp?.ebenen || []).map(x => String(x || '').toLowerCase()).join(' ');
      return NON_SCHRAF_TOKENS.some(t => e.includes(t));
    };

    return sortedIds.filter(col => {
      const sp = spalten[col];
      if (!sp) return false;
      if (sp.empty) return false;
      const lv = sp.ebenen || [];
      if (!lv.some(x => String(x || '').trim())) return false;
      if (isNonSchrafHeader(sp)) return false;
      // Datentext-only-Spalten (haben nur konkrete Werte, keine Schraffur) raus -
      // ABER ein einzelnes "X" oder ein einbuchstabiger NKS-Marker bleibt drin.
      let hasSchraf = false;
      let hasDataText = false;
      for (const f of fbfs) {
        const fills = rawFills[f.lokalname] || {};
        const v = fills[col];
        const sch = f.schraffuren?.[col];
        if (sch === 'haken' || v === 'schr') hasSchraf = true;
        else if (v) {
          const s = String(v).trim();
          const isMarker = ['M','Z','A','K','R'].includes(s.toUpperCase());
          if (s.length > 2 || isMarker) hasDataText = true;
        }
      }
      if (hasDataText && !hasSchraf) return false;
      return true;
    });
  }

  /** Richtet das BLATT einer Spalte (letztes nicht-leeres Header-Element) auf die
   *  unterste Ebene (maxLevel) aus. Hintergrund: einige D_Parser-Spalten kamen mit
   *  verstuemmelten `ebenen` - der Kopf wurde auf wenige Ebenen zusammengeklebt, so
   *  dass der Blatt-Wert (z.B. "S") nicht in der untersten Header-Zeile sitzt,
   *  sondern weiter oben. Der Renderer setzt dann leere weisse Zellen darunter und
   *  die Schraffuren wirken um eine Spalte verrutscht (Tieflehm-Fahlerde Spalte 16:
   *  ["Koernung in der Delta-Zone","S"] statt 5 Ebenen mit "S" ganz unten).
   *
   *  Korrigiert NUR die Position (Anzeige), erfindet KEINEN Inhalt: die fuehrenden
   *  Gruppen-Labels bleiben oben, das Blatt wandert nach unten, die Luecke in die
   *  Mitte. Ein EINZELNES Label (eine Ebene) bleibt unveraendert - es soll als
   *  Top-Gruppe ueber die volle Hoehe rowspannen (bestehendes computeHeaderRows-
   *  Verhalten fuer ['Solo']). Bereits korrekt unten sitzende Blaetter (Laenge ==
   *  maxLevel ohne nachlaufende Leerzeilen) bleiben ebenfalls unveraendert. */
  bottomAlignLeaf(ebenen: string[], maxLevel: number): string[] {
    if (!Array.isArray(ebenen)) return ebenen;
    // nachlaufende Leer-Ebenen wegschneiden (Blatt soll das letzte ECHTE Element sein)
    let end = ebenen.length;
    while (end > 0 && !String(ebenen[end - 1] ?? '').trim()) end--;
    const trimmed = ebenen.slice(0, end);
    // 0 oder 1 echte Ebene: nichts auszurichten (Einzel-Label rowspannt als Top-Gruppe)
    if (trimmed.length <= 1) return ebenen;
    // Blatt schon in der untersten Zeile -> unveraendert
    if (trimmed.length >= maxLevel) return trimmed.slice(0, maxLevel);
    // Gruppen oben lassen, Blatt nach unten, Luecke in die Mitte
    const leaf = trimmed[trimmed.length - 1];
    const head = trimmed.slice(0, trimmed.length - 1);
    const gap = Array(maxLevel - trimmed.length).fill('');
    return [...head, ...gap, leaf];
  }

  /** Header-Zeilen mit colspan-Gruppierung (konsekutive Spalten mit gleichem
   *  Label auf gleicher Ebene werden zur selben TH-Zelle zusammengefasst).
   *  Rowspan-Optimierung fuer Top-Gruppen ohne Sub-Header. */
  private computeHeaderRows(
    cols: string[],
    lvls: Record<string, string[]>,
    maxLevel: number,
  ): HeaderGroup[][] {
    const rows: HeaderGroup[][] = [];
    for (let lvl = 0; lvl < maxLevel; lvl++) {
      const row: HeaderGroup[] = [];
      for (const c of cols) {
        const label = lvls[c]?.[lvl] || '';
        const prev = row[row.length - 1];
        if (prev && prev.label === label) {
          prev.cols.push({ id: c, label });
        } else {
          row.push({ label, cols: [{ id: c, label }] });
        }
      }
      rows.push(row);
    }
    // Rowspan-Optimierung
    if (rows.length > 1) {
      for (const grp of rows[0]) {
        const ids = new Set(grp.cols.map(c => c.id));
        let span = 1;
        for (let lvl = 1; lvl < rows.length; lvl++) {
          let allEmpty = true;
          for (const subGrp of rows[lvl]) {
            for (const sc of subGrp.cols) {
              if (ids.has(sc.id) && (subGrp.label || '').trim()) { allEmpty = false; break; }
            }
            if (!allEmpty) break;
          }
          if (allEmpty) span++; else break;
        }
        grp.rowspan = span;
      }
      // absorbierte Zellen aus den unteren Zeilen entfernen
      for (const grp of rows[0]) {
        if ((grp.rowspan || 1) > 1) {
          const ids = new Set(grp.cols.map(c => c.id));
          for (let lvl = 1; lvl < (grp.rowspan || 1); lvl++) {
            rows[lvl] = rows[lvl]
              .map(subGrp => ({ ...subGrp, cols: subGrp.cols.filter(c => !ids.has(c.id)) }))
              .filter(subGrp => subGrp.cols.length > 0);
          }
        }
      }
      // Nach der Absorption koennen Gruppen entstehen, deren Spalten im GRID nicht
      // mehr zusammenhaengen: eine rowspan-absorbierte Spalte (z.B. CaCO3) liegt
      // zwischen ihnen. Ein einzelnes <th colspan> kann diese Luecke NICHT
      // ueberspringen - der Browser kollidiert mit der rowspan-Zelle und schiebt
      // alle Folgespalten (und damit die Schraffuren im Body) um eine Spalte; im
      // Header bleiben kaputte leere Zellen. Darum jede Gruppe in zusammen-
      // haengende Laeufe nach Original-Spaltenposition aufteilen.
      const posOf = new Map<string, number>();
      cols.forEach((c, i) => posOf.set(c, i));
      for (let lvl = 0; lvl < rows.length; lvl++) {
        const split: HeaderGroup[] = [];
        for (const grp of rows[lvl]) {
          let run: { id: string; label: string }[] = [];
          let prevPos = -2;
          for (const cell of grp.cols) {
            const p = posOf.get(cell.id) ?? -1;
            if (run.length && p !== prevPos + 1) {
              split.push({ ...grp, cols: run });
              run = [];
            }
            run.push(cell);
            prevPos = p;
          }
          if (run.length) split.push({ ...grp, cols: run });
        }
        rows[lvl] = split;
      }
    }
    return rows;
  }

  /** Verbindet leere Header-Zellen: erst vertikal je Spalte (mehrere gestapelte
   *  Leerzellen -> EINE durchgehende per rowspan), dann horizontal benachbarte
   *  Leer-Laeufe mit identischem Zeilenbereich (-> colspan). So bleibt im leeren
   *  Zwischenraum ueber den Blaettern (z.B. ueber "I + al S"/"S"/"L"/"U" oder
   *  "I - VII"/"VIII") keine Trennlinie mehr. Nicht-leere Zellen bleiben
   *  unveraendert; nur das Leerraster wird neu zusammengefasst. */
  mergeEmptyCells(rows: HeaderGroup[][], cols: string[]): HeaderGroup[][] {
    const maxLevel = rows.length;
    if (maxLevel <= 1) return rows;
    const n = cols.length;
    const posOf = new Map<string, number>();
    cols.forEach((c, i) => posOf.set(c, i));
    // Belegung durch NICHT-leere Zellen inkl. ihrer rowspan-Ausdehnung; die
    // leeren Zellen verwerfen wir und bauen sie frisch zusammengefasst neu.
    const occ: boolean[][] = Array.from({ length: maxLevel }, () => new Array(n).fill(false));
    const kept: { row: number; grp: HeaderGroup }[] = [];
    for (let r = 0; r < maxLevel; r++) {
      for (const g of rows[r]) {
        if (!(g.label || '').trim()) continue;
        kept.push({ row: r, grp: g });
        const span = g.rowspan && g.rowspan > 1 ? g.rowspan : 1;
        for (const cell of g.cols) {
          const p = posOf.get(cell.id);
          if (p == null) continue;
          for (let rr = r; rr < Math.min(maxLevel, r + span); rr++) occ[rr][p] = true;
        }
      }
    }
    // Vertikale Leer-Laeufe je Spalte sammeln.
    const runs: { start: number; end: number; p: number }[] = [];
    for (let p = 0; p < n; p++) {
      let start = -1;
      for (let r = 0; r < maxLevel; r++) {
        if (!occ[r][p]) { if (start < 0) start = r; }
        else if (start >= 0) { runs.push({ start, end: r - 1, p }); start = -1; }
      }
      if (start >= 0) runs.push({ start, end: maxLevel - 1, p });
    }
    // Horizontal benachbarte Laeufe mit identischem (start,end) zu colspan mergen.
    runs.sort((a, b) => a.start - b.start || a.p - b.p);
    const emptyCells: { row: number; grp: HeaderGroup }[] = [];
    let i = 0;
    while (i < runs.length) {
      const { start, end } = runs[i];
      const group = [runs[i]];
      let j = i + 1;
      while (j < runs.length && runs[j].start === start && runs[j].end === end
             && runs[j].p === group[group.length - 1].p + 1) {
        group.push(runs[j]); j++;
      }
      const grp: HeaderGroup = { label: '', cols: group.map(rn => ({ id: cols[rn.p], label: '' })) };
      if (end > start) grp.rowspan = end - start + 1;
      emptyCells.push({ row: start, grp });
      i = j;
    }
    // Zeilen neu aufbauen, je Zeile nach linkester Spaltenposition sortiert.
    const out: HeaderGroup[][] = Array.from({ length: maxLevel }, () => []);
    for (const { row, grp } of [...kept, ...emptyCells]) out[row].push(grp);
    for (const row of out) row.sort((a, b) => (posOf.get(a.cols[0].id) ?? 0) - (posOf.get(b.cols[0].id) ?? 0));
    return out;
  }

  /** Header-Orientierung vergeben — einheitlich pro Geschwister-Gruppe.
   *
   *  Der Header ist ein Baum: jede Zelle in Ebene N ist Kind genau einer Zelle
   *  in Ebene N-1 (die ihre Spalten ueberspannt). "Eine Gruppe" = alle
   *  Geschwister mit DEMSELBEN Elternteil ("Unterebenen einer Ebene").
   *
   *  Regel (Anwender): Orientierung darf zwischen Gruppen wechseln, MUSS aber
   *  innerhalb einer Gruppe einheitlich sein. Konservativ: sobald auch nur EIN
   *  Geschwister horizontal nicht passt, kippt die GANZE Gruppe vertikal - so
   *  wird nie ein Label gequetscht/abgeschnitten.
   *
   *  WICHTIG: Top-Zellen (Ebene 0) haben KEINEN gemeinsamen Elternteil und
   *  werden daher EINZELN entschieden - "Koernung" darf horizontal stehen, auch
   *  wenn der Nachbar "NB-Stufe im Aufschluss" vertikal kippt. (Frueher faelsch-
   *  lich als eine Gruppe behandelt -> breite Gruppen kippten unnoetig.) */
  private assignOrientations(rows: HeaderGroup[][]): void {
    for (let lvl = 0; lvl < rows.length; lvl++) {
      // Schriftgroesse haengt an Leaf vs. Gruppe - die letzte Zeile ist Leaf.
      const isLeafRow = lvl === rows.length - 1;
      const buckets = new Map<string, HeaderGroup[]>();
      let orphan = 0;
      for (const grp of rows[lvl]) {
        let key: string;
        if (lvl === 0) {
          key = `o${orphan++}`; // Ebene 0: jede Top-Zelle ist ihre eigene Gruppe
        } else {
          const firstId = grp.cols[0]?.id;
          // Elternteil = NAECHSTGELEGENE Zelle DARUEBER, deren Spalten-Set diese
          // enthaelt. Wichtig: nach oben weitersuchen, damit rowspan-absorbierte
          // Zwischenebenen uebersprungen werden - sonst findet ein Leaf seinen
          // rowspan-Elternteil nicht, wird Waise und einzeln entschieden; dann
          // ist die Gruppe nicht mehr einheitlich (z.B. kurzes "I" horizontal,
          // Geschwister vertikal).
          key = `o${orphan++}`;
          for (let up = lvl - 1; up >= 0; up--) {
            const idx = rows[up].findIndex(p => p.cols.some(c => c.id === firstId));
            if (idx >= 0) { key = `${up}:${idx}`; break; }
          }
        }
        const bucket = buckets.get(key);
        if (bucket) bucket.push(grp);
        else buckets.set(key, [grp]);
      }
      for (const siblings of buckets.values()) this.applyGroupOrientation(siblings, isLeafRow);
    }
  }

  /** Eine Geschwister-Gruppe einheitlich orientieren: horizontal nur, wenn
   *  ALLE Mitglieder horizontal passen, sonst alle vertikal. */
  private applyGroupOrientation(siblings: HeaderGroup[], isLeaf: boolean): void {
    const allHorizontal = siblings.every(g => this.fitsHorizontal(g.label, g.cols.length, isLeaf));
    for (const g of siblings) g.orientation = allHorizontal ? 'h' : 'v';
  }

  /** Letzter Fallback der Fit-Kaskade (horizontal -> vertikal -> Umbruch ->
   *  SCHRIFT VERKLEINERN): Passt ein bereits orientiertes Label trotz Umbruch
   *  nicht in die Zelle, wird die Schrift schrittweise verkleinert.
   *
   *  Relevant v.a. fuer VERTIKALE Labels: ihre umgebrochenen Zeilen stapeln sich
   *  in der Breite (nCols * SCHRAF_COL_W). Brauchen sie mehr Zeilen, als breit
   *  Platz ist, wuerden sie abgeschnitten -> Schrift kleiner, bis es passt.
   *  Horizontale Labels wachsen dagegen in der HOEHE (Zeile waechst) und passen
   *  durch die Orientierungswahl (laengstes Wort passt in die Breite) ohnehin. */
  private fitFontSizes(rows: HeaderGroup[][]): void {
    for (let lvl = 0; lvl < rows.length; lvl++) {
      const isLeaf = lvl === rows.length - 1;
      const base = isLeaf ? HEADER_FONT_LEAF : HEADER_FONT_GROUP;
      for (const grp of rows[lvl]) {
        grp.fontPx = undefined;
        if (grp.orientation !== 'v') continue; // horizontal waechst in der Hoehe
        const width = grp.cols.length * SCHRAF_COL_W - 2; // Breite fuer den Zeilen-Stapel
        let f = base;
        for (; f > MIN_HEADER_FONT; f--) {
          const total = this.measureLabelWidth(grp.label, isLeaf, f); // 1-zeilige Laenge
          const lines = Math.max(1, Math.ceil(total / VERT_LINE_CAP));
          const lineH = f * 1.2; // Quer-Platz je vertikaler Zeile
          if (lines * lineH <= width) break; // Stapel passt in die Breite
        }
        if (f < base) grp.fontPx = f;
      }
    }
  }

  /** Markiert die BLATT-Header (tiefstes Level) als sortierbar. Eine Header-Zelle
   *  ist ein Blatt, wenn sie die unterste Header-Zeile erreicht - also ihre
   *  Startzeile + rowspan == maxLevel - und ein nicht-leeres Label hat. Genau
   *  diese Zellen stehen direkt ueber EINER konkreten Schraffur-Spalte (bzw.
   *  ueber den per colspan zusammengefassten Spalten desselben Blatt-Labels) und
   *  duerfen zum Sortieren angeklickt werden. Gruppen-Header (mit Unter-Headern
   *  darunter) und leere Zwischenzellen bleiben unklickbar. */
  markSortableHeaders(rows: HeaderGroup[][], maxLevel: number): void {
    for (let r = 0; r < rows.length; r++) {
      for (const g of rows[r]) {
        const span = g.rowspan && g.rowspan > 1 ? g.rowspan : 1;
        const isLeaf = !!(g.label || '').trim() && r + span === maxLevel;
        g.sortable = isLeaf;
        g.sortCols = isLeaf ? g.cols.map(c => c.id) : undefined;
      }
    }
  }

  /** Effektive Hinweis-Sektionen: e.sections PLUS die Legacy-Top-Level-Felder
   *  aus dem D_Parser-Rohimport, die der Editor sonst erst beim Speichern nach
   *  e.sections migriert. So zeigt die Preview dieselben Bloecke wie der Editor -
   *  auch fuer noch nicht migrierte Eintraege. Read-only: der Eintrag selbst und
   *  seine Sub-Objekte werden NICHT mutiert (flache Kopien). Dedup nach Titel. */
  private buildEffectiveSections(e: BodensteckbriefEntry): Array<{ kind: string; title: string; content: any }> {
    // Exakter Titel-Schluessel: NUR lowercase+trim. KEINE Tilgung von Leerzeichen/
    // Bindestrichen - sonst kollabieren eigenstaendige Sektionen wie
    // "Substrathinweis" und "Substrat-Hinweis" auf denselben Schluessel und eine
    // fiele still weg (Regel: ALLE Sektionen anzeigen).
    const key = (s: string) => (s || '').toLowerCase().trim();
    const out: Array<{ kind: string; title: string; content: any }> =
      (e.sections || []).map(s => ({ ...s }));
    const hasTitle = (t: string) => out.some(s => key(s.title) === key(t));
    // Haupt-Humus ist EXAKT "Humus"/"Humushinweise". Nummerierte Varianten
    // ("Humushinweise #2", "#3") sind eigenstaendige Sektionen und duerfen die
    // Haupt-Humushinweise NICHT aufsaugen.
    const findHumus = () => out.find(s => key(s.title) === 'humushinweise' || key(s.title) === 'humus');

    // extra_left_sections (Eisen, Baender, sonstige generische Hinweise) - nur
    // ergaenzen, was per Titel noch nicht da ist.
    const els = (e as any).extra_left_sections;
    if (Array.isArray(els)) {
      for (const s of els) if (s && s.title && !hasTitle(s.title)) out.push({ ...s });
    }
    // Humus als Key-Value-Objekt (humushinweise) -> items.
    const hi = (e as any).humushinweise;
    if (hi && typeof hi === 'object' && !Array.isArray(hi) && Object.keys(hi).length) {
      let humus = findHumus();
      if (!humus) { humus = { kind: 'key_value', title: 'Humushinweise', content: {} }; out.push(humus); }
      humus.content = { ...(humus.content || {}) };
      humus.content.items = { ...hi, ...(humus.content.items || {}) };
    }
    // Humus als Zeilen (humushinweise_lines) -> lines.
    const hl = (e as any).humushinweise_lines;
    if (Array.isArray(hl) && hl.length) {
      let humus = findHumus();
      if (!humus) { humus = { kind: 'text', title: 'Humushinweise', content: {} }; out.push(humus); }
      humus.content = { ...(humus.content || {}) };
      humus.content.lines = [...(humus.content.lines || []), ...hl];
    }
    // Hydromorphie (hydromorphiehinweise) - nur falls noch keine Hydromorphie-Sektion.
    const hy = e.hydromorphiehinweise;
    if (Array.isArray(hy) && hy.length && !out.some(s => key(s.title).startsWith('hydromorph'))) {
      out.push({ kind: 'text', title: 'Hydromorphiehinweise', content: { lines: [...hy] } });
    }
    return out;
  }

  /** Auffangnetz fuer die Regel "ALLE Daten einer HBF werden dargestellt":
   *  jedes Eintrags-Feld mit Inhalt, das NICHT in KNOWN_ENTRY_KEYS steht (also
   *  nirgends sonst gerendert/gefaltet wird), wird hier generisch erfasst -
   *  Arrays als Zeilen, Objekte als key/value, Primitive als Zeile, alles andere
   *  als JSON. So kann kein (auch kein kuenftiges) Parser-Feld stumm verloren
   *  gehen. */
  private collectUnknownData(e: BodensteckbriefEntry): Array<{ title: string; lines: string[]; items: Array<{ key: string; value: string }> }> {
    const out: Array<{ title: string; lines: string[]; items: Array<{ key: string; value: string }> }> = [];
    const str = (v: any) => (v !== null && typeof v === 'object') ? JSON.stringify(v) : String(v);
    for (const [key, v] of Object.entries(e || {})) {
      if (KNOWN_ENTRY_KEYS.has(key)) continue;
      // leere/leere-Container ueberspringen
      if (v === null || v === undefined || v === '') continue;
      if (Array.isArray(v) && v.length === 0) continue;
      if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) continue;

      const block = { title: this.humanizeKey(key), lines: [] as string[], items: [] as Array<{ key: string; value: string }> };
      if (Array.isArray(v)) {
        for (const it of v) block.lines.push(str(it));
      } else if (typeof v === 'object') {
        for (const [ik, iv] of Object.entries(v)) block.items.push({ key: ik, value: str(iv) });
      } else {
        block.lines.push(String(v));
      }
      out.push(block);
    }
    return out;
  }

  /** Feldname -> lesbarer Titel: "hinweise"-Suffix weg, _ zu Leerzeichen, erster
   *  Buchstabe gross. z.B. "eisenhinweise" -> "Eisen", "foo_bar" -> "Foo bar". */
  private humanizeKey(key: string): string {
    const t = String(key || '').replace(/hinweise$/i, '').replace(/[_]+/g, ' ').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : key;
  }

  /** Sections in: Humus / Hydromorphie / sonstige Hinweise links unten. */
  private classifySections(sections: Array<{ kind: string; title: string; content: any }>): void {
    const norm = (s: string) => (s || '').toLowerCase().replace(/[\s-]/g, '');
    for (const s of sections) {
      const t = norm(s.title);
      // NUR der Haupt-Humus ("Humus"/"Humushinweise") landet im dedizierten
      // Humus-Block. Nummerierte Varianten ("Humushinweise #2", "#3") fallen
      // bewusst in den else-Zweig und werden als eigene, BETITELTE Bloecke
      // gerendert - sonst verschwaenden sie unsichtbar im Haupt-Humus-Block.
      if (t === 'humus' || t === 'humushinweise') {
        const c = s.content || {};
        if (c.items && typeof c.items === 'object') {
          for (const [k, v] of Object.entries(c.items)) {
            this.humusItems.push({ key: k, value: String(v) });
          }
        }
        if (Array.isArray(c.lines)) this.humusLines.push(...c.lines.map((x: any) => String(x)));
        continue;
      }
      if (t.startsWith('hydromorph')) {
        const c = s.content || {};
        if (Array.isArray(c.lines)) this.hydromorphieLines.push(...c.lines.map((x: any) => String(x)));
        continue;
      }
      if (t === 'hauptbodenform' || t === 'substrat') continue; // separat dargestellt
      // alles andere wandert in den linken Stapel
      const lines: string[] = [];
      const items: Array<{ key: string; value: string }> = [];
      const c = s.content || {};
      if (c.items && typeof c.items === 'object' && !Array.isArray(c.items)) {
        for (const [k, v] of Object.entries(c.items)) items.push({ key: k, value: String(v) });
      } else if (Array.isArray(c.items)) {
        for (const it of c.items) lines.push(String(it));
      }
      if (Array.isArray(c.lines)) lines.push(...c.lines.map((x: any) => String(x)));
      else if (typeof c.text === 'string') lines.push(c.text);
      if (lines.length || items.length) {
        this.extraLeftSections.push({
          title: (s.title || '').replace(/hinweise$/i, '').trim(),
          kind: s.kind, lines, items,
        });
      }
    }
  }

  // ========== Helpers fuer Template ==========

  /** Schraffur-Zustand fuer (fbf, col). Liefert '' | 'haken' | 'x' | '?' | <freitext>. */
  cellState(f: BodensteckbriefFbfRow, col: string): string {
    const direct = f.schraffuren?.[col];
    if (direct) return direct;
    const fills = (this.entry?.raw_fills || {})[f.lokalname] || {};
    const v = fills[col];
    if (!v) return '';
    if (v === 'schr') return 'haken';
    const up = String(v).toUpperCase();
    if (up === 'X') return 'x';
    if (v === '?') return '?';
    return String(v); // Freitext (z.B. "M", "II, IV")
  }

  /** Klassen + Text fuer eine Schraffur-Zelle. */
  cellClass(state: string): string {
    // pv-schraf-cell = quadratische Schraffur-Zelle (18x18, siehe CSS, Spalten
    // fix 18px + Text-Spalten flexibel); die Zustands-Klassen kommen additiv dazu.
    if (state === 'haken') return 'pv-schraf-cell pv-shaded';
    if (state === 'x' || state === '?') return 'pv-schraf-cell col-center-cell';
    if (state) return 'pv-schraf-cell col-center-cell pv-text-cell';
    return 'pv-schraf-cell';
  }
  cellText(state: string): string {
    if (state === 'haken') return '';
    if (state === 'x') return 'X';
    if (state === '?') return '?';
    return state || '';
  }

  /* Hinweis: Mehrzeilig-vertikale Header werden per CSS gerendert - die max.
     vertikale Zeilenlaenge ist begrenzt (height auf .vh > span), damit lange
     Labels in MEHR (kuerzere) Zeilen brechen und die Breite nutzen; text-wrap:
     balance verteilt die Zeilen dann gleichmaessig. */
  /** Header-Label fuer die ANZEIGE aufbereiten: normale Bindestriche (U+002D)
   *  werden zu geschuetzten Bindestrichen (U+2011). Dadurch bricht der Browser
   *  Zeilen BEVORZUGT an Leerzeichen um und zerreisst zusammengehoerige Begriffe
   *  wie "A-Horizont" nicht am "-". Passt ein Wort gar nicht in die Spalte,
   *  waehlt fitsHorizontal ohnehin vertikal - der Bindestrich-Umbruch entfaellt
   *  damit als Notloesung. Die Breitenmessung nutzt weiter das Roh-Label (gleiche
   *  Glyphenbreite), bleibt also konsistent zur Darstellung. */
  displayLabel(label: string): string {
    return String(label || '').replace(/-/g, '‑');
  }

  /** Kann ein Label horizontal dargestellt werden? Eine Header-Gruppe hat
   *  `nCols * SCHRAF_COL_W` px Breite.
   *   1. Passt das Label komplett auf EINE Zeile -> horizontal (egal wie breit).
   *   2. Sonst Umbruch nur bei MEHRSPALTIGEN (breiten) Zellen, und nur wenn das
   *      laengste Wort in die Breite passt ("NB-Stufe im Aufschluss" -> 3 Zeilen).
   *   3. Schmale 1-Spalten-Header lieber vertikal als nach 1-2 Zeichen umbrechen
   *      ("> 40", "15 - 40", "< 15") -> vertikal.
   *  `isLeaf` steuert die Schriftgroesse (Leaf 11px, Gruppe 12px fett). */
  fitsHorizontal(label: string, nCols: number, isLeaf = false): boolean {
    const available = nCols * SCHRAF_COL_W - HEADER_CELL_PAD;
    // 1. passt auf eine Zeile?
    if (this.measureLabelWidth(label, isLeaf) <= available) return true;
    // 2./3. Umbruch nur sinnvoll bei mehrspaltigen Zellen.
    if (nCols < 2) return false;
    return this.longestWordWidth(label, isLeaf) <= available;
  }

  /** Breite des breitesten whitespace-getrennten Wortes in px. Bewusst NICHT an
   *  Bindestrichen splitten - "15-40" soll als ein Wort gelten (sonst brichts zu
   *  "15-"/"40" um), "NB-Stufe" passt ueber das nachfolgende Leerzeichen ohnehin. */
  private longestWordWidth(label: string, isLeaf: boolean): number {
    const words = String(label || '').split(/\s+/).filter(Boolean);
    let max = 0;
    for (const w of words) max = Math.max(max, this.measureLabelWidth(w, isLeaf));
    return max;
  }

  /** Textbreite eines Labels in px. Bevorzugt echte Canvas-Messung (beruecksich-
   *  tigt proportionale Schrift: "WWWW" breiter als "iiii"); ohne Canvas (z.B.
   *  Unit-Test) grobe Schaetzung pro Zeichen. */
  private measureLabelWidth(label: string, isLeaf: boolean, fontPx?: number): number {
    const text = label || '';
    if (!text) return 0;
    const baseFont = isLeaf ? HEADER_FONT_LEAF : HEADER_FONT_GROUP;
    const size = fontPx ?? baseFont;
    const ctx = this.getMeasureCtx();
    if (ctx) {
      ctx.font = `${isLeaf ? '' : '600 '}${size}px ${this._measureFamily}`;
      const w = ctx.measureText(text).width;
      if (w > 0) return w;
    }
    // Fallback ohne Canvas: ~0.5px/px-Schrift pro Zeichen (Leaf) bzw. ~0.59 (Gruppe fett).
    return text.length * size * (isLeaf ? 0.5 : 0.59);
  }

  /** Lazy-Canvas-Kontext (einmal erzeugt, Family aus dem Body uebernommen). */
  private getMeasureCtx(): CanvasRenderingContext2D | null {
    if (this._measureCtx !== undefined) return this._measureCtx;
    try {
      if (typeof document === 'undefined') return (this._measureCtx = null);
      this._measureCtx = document.createElement('canvas').getContext('2d');
      if (typeof getComputedStyle !== 'undefined' && document.body) {
        this._measureFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';
      }
    } catch {
      this._measureCtx = null;
    }
    return this._measureCtx;
  }

  /** Diagramme: das Entry kann horizont_diagramme: [{horizonte,zonen},...]
   *  oder ein einzelnes horizont: {horizonte,zonen} haben. */
  get diagramme(): Array<{ horizonte: any[]; zonen: any[] }> {
    const e = this.entry;
    if (!e) return [];
    if (Array.isArray(e.horizont_diagramme) && e.horizont_diagramme.length) {
      return e.horizont_diagramme.map((d: any) => ({
        horizonte: d?.horizonte || [], zonen: d?.zonen || [],
      }));
    }
    if (e.horizont && (e.horizont.horizonte || e.horizont.zonen)) {
      return [{ horizonte: e.horizont.horizonte || [], zonen: e.horizont.zonen || [] }];
    }
    return [];
  }

  /** Symbol-Text aus horizont/zone-Eintraegen (kann String oder Objekt sein). */
  symOf(x: any): string {
    if (!x) return '';
    if (typeof x === 'string') return x;
    return x.symbol || x.name || x.text || '';
  }
  noteOf(x: any): string {
    if (x && typeof x === 'object') return x.note || '';
    return '';
  }

  /** Pfeil-Rendering: nur in der ERSTEN Leerzeile eines Gaps, der direkt
   *  unter einer gefuellten Zelle anfaengt. Niemals oben (rowIdx == 0) -
   *  Pfeile sollen erst nach dem ersten Eintrag auftauchen. Folgende
   *  konsekutive Leerzeilen bleiben sichtbar leer, damit ein mehrzeiliger
   *  Gap einen einzelnen Pfeil und darunter freien Raum zeigt. */
  showArrow(diag: { horizonte: any[]; zonen: any[] }, rowIdx: number, kind: 'horizonte' | 'zonen'): boolean {
    if (rowIdx === 0) return false;
    const arr = (diag as any)[kind] || [];
    if (this.symOf(arr[rowIdx])) return false;
    if (!this.symOf(arr[rowIdx - 1])) return false;
    for (let j = rowIdx + 1; j < arr.length; j++) {
      if (this.symOf(arr[j])) return true;
    }
    return false;
  }

  /** Fussnoten - akzeptiert Map (marker -> text) oder Liste/Zeilen. */
  get fussnotenLines(): string[] {
    const f = this.entry?.fussnoten;
    if (!f) return [];
    const out: string[] = [];
    if (f.items && typeof f.items === 'object' && !Array.isArray(f.items)) {
      for (const [k, v] of Object.entries(f.items)) out.push(`${k} ${v}`);
    } else if (Array.isArray(f.items)) {
      for (const it of f.items) out.push(String(it));
    }
    if (Array.isArray(f.lines)) for (const ln of f.lines) out.push(String(ln));
    return out;
  }

  /** Substrat-Layer (mehrschichtig) oder Fallback auf single-layer. */
  get substratLayers(): Array<{ kuerzel: string; beschreibung: string }> {
    const e = this.entry;
    if (!e) return [];
    if (Array.isArray(e.substrat_layers) && e.substrat_layers.length) {
      return e.substrat_layers.map(l => ({
        kuerzel: l?.kuerzel || '',
        beschreibung: l?.beschreibung || '',
      }));
    }
    const kuerzel = (e.substrat_kuerzel || [])[0] || e.hauptbodenform?.substratgruppe || '';
    return [{ kuerzel, beschreibung: '' }];
  }

  /** Substrattexte tragen im Original Absaetze, die in der Vorlage fast immer
   *  an Semikola haengen (jeder Teilsatz ein eigener Absatz, z.B.
   *  "... oder schwerer;" / "bis mindestens 20 cm Tiefe karbonatfrei, ...").
   *  Wir brechen die Beschreibung an bestehenden Zeilenumbruechen UND an
   *  Semikola in Absaetze auf und haengen das trennende Semikolon wieder an
   *  den jeweiligen Absatz an - so wie es im gedruckten Steckbrief steht. */
  substratParagraphs(beschreibung: string | undefined): string[] {
    const paragraphs: string[] = [];
    for (const line of String(beschreibung || '').split(/\r?\n/)) {
      const segments = line.split(';');
      for (let i = 0; i < segments.length; i++) {
        const text = segments[i].trim();
        if (!text) continue;
        // Semikolon nur wieder anhaengen, wenn im Original eines folgte.
        const followedBySemicolon = i < segments.length - 1;
        paragraphs.push(followedBySemicolon ? `${text};` : text);
      }
    }
    return paragraphs;
  }

  /** name/abkuerzung koennen mit " / " getrennte Split-HBFs enthalten. */
  splitSlashed(s: string | undefined): string[] {
    return String(s || '').split(' / ').map(x => x.trim()).filter(Boolean);
  }

  /** Iterations-Helfer: [0..n-1]. Brauchen wir, weil der Angular-Template-
   *  Parser bei `[].constructor(N)` zickt - eine echte Methode ist robuster
   *  und macht das Template lesbarer. */
  range(n: number): number[] {
    const len = Math.max(0, Math.floor(n || 0));
    return Array.from({ length: len }, (_v, i) => i);
  }

  // ========== Lokale Sortierung der FBF-Zeilen ==========

  /** Click auf einen Fix-Spalten-Header (Lokalname/Abk./NKS/5tel/Forstbetrieb):
   *  erster Klick auf neues Feld -> aufsteigend, zweiter Klick -> absteigend,
   *  dritter Klick -> Original-Reihenfolge zurueck. So bleibt jeder Header
   *  zugleich "Reset"-Knopf; zusaetzlich gibt es den expliziten Reset-Button. */
  setSort(field: '' | 'lokalname' | 'abkuerzung' | 'nahrkraftstufe' | 'funftelstufe' | 'forstbetrieb'): void {
    if (!field) { this.resetSort(); return; }
    if (this.sortField !== field) {
      this.sortField = field;
      this.sortSchrafCols = [];
      this.sortDir = 'asc';
      return;
    }
    if (this.sortDir === 'asc') { this.sortDir = 'desc'; return; }
    this.resetSort(); // dritter Klick: zurueck zur Original-Reihenfolge
  }

  /** Click auf einen Blatt-Header (tiefstes Level) einer Schraffur-Spalte.
   *  Gleiche Toggle-Mechanik wie setSort: asc -> desc -> Reset. `cols` sind die
   *  physischen Spalten-IDs, die diese Blattzelle abdeckt (per colspan ggf. >1).
   *  asc = markierte Zeilen (Schraffur/Eintrag in dieser Spalte) zuerst. */
  setSortSchraf(cols: string[] | undefined): void {
    if (!cols || !cols.length) return;
    const id = cols.join(',');
    if (this.sortField !== 'schraf' || this.sortSchrafCols.join(',') !== id) {
      this.sortField = 'schraf';
      this.sortSchrafCols = cols;
      this.sortDir = 'asc';
      return;
    }
    if (this.sortDir === 'asc') { this.sortDir = 'desc'; return; }
    this.resetSort(); // dritter Klick: zurueck zur Original-Reihenfolge
  }

  /** Setzt jede Sortierung (Fix- wie Schraffur-Spalten) auf die
   *  Original-Reihenfolge des Steckbriefs zurueck. */
  resetSort(): void {
    this.sortField = '';
    this.sortSchrafCols = [];
    this.sortDir = 'asc';
  }

  /** True, sobald irgendeine Sortierung aktiv ist - steuert die Sichtbarkeit des
   *  Reset-Buttons. */
  get hasActiveSort(): boolean {
    return this.sortField !== '';
  }

  /** Pfeil/Indikator fuer einen Fix-Spalten-Sort-Header (kleines UI-Helferchen). */
  sortIcon(field: string): string {
    if (this.sortField !== field) return '';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  /** Ist diese Schraffur-Blattspalte (durch ihre Spalten-IDs identifiziert)
   *  gerade die aktive Sortier-Spalte? */
  isSchrafSorted(cols: string[] | undefined): boolean {
    if (this.sortField !== 'schraf' || !cols || !cols.length) return false;
    return this.sortSchrafCols.join(',') === cols.join(',');
  }

  /** Pfeil/Indikator fuer eine Schraffur-Blattspalte (analog sortIcon). */
  sortIconSchraf(cols: string[] | undefined): string {
    if (!this.isSchrafSorted(cols)) return '';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  /** FBF-Zeilen in der aktuellen Anzeige-Reihenfolge. Ohne Sort identisch zur
   *  Original-Reihenfolge im Steckbrief - Schraffuren sind Teil der Row-Objekte,
   *  wandern also automatisch mit. */
  get sortedFbfs(): BodensteckbriefFbfRow[] {
    const arr = this.entry?.feinbodenformen || [];
    if (!this.sortField) return arr;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    // Schraffur-Spalte: markierte Zeilen zuerst (asc), sonst Originalreihenfolge
    // (stabiler Sort haelt gleichrangige Zeilen in ihrer Ursprungsfolge).
    if (this.sortField === 'schraf') {
      const cols = this.sortSchrafCols;
      return [...arr].sort((a, b) => (this.schrafWeight(a, cols) - this.schrafWeight(b, cols)) * dir);
    }
    const f = this.sortField;
    return [...arr].sort((a, b) => {
      const av = String((a as any)[f] || '');
      const bv = String((b as any)[f] || '');
      // NKS folgt der fachlichen Reihenfolge (R,K,M,Z,A), nicht alphabetisch.
      const cmp = f === 'nahrkraftstufe'
        ? this.compareNks(av, bv)
        : av.localeCompare(bv, 'de', { numeric: true, sensitivity: 'base' });
      return cmp * dir;
    });
  }

  /** Sortier-Gewicht einer FBF-Zeile fuer eine Schraffur-Spalte(ngruppe):
   *  0 = markiert (mind. eine der Spalten hat eine Schraffur/einen Eintrag),
   *  1 = leer. So landen markierte Zeilen bei asc oben. */
  private schrafWeight(f: BodensteckbriefFbfRow, cols: string[]): number {
    for (const c of cols) {
      if (this.cellState(f, c)) return 0;
    }
    return 1;
  }

  /** Vergleicht zwei Naehrkraftstufen nach NKS_ORDER (R,K,M,Z,A) statt
   *  alphabetisch. Unbekannte Werte landen hinter den bekannten, leere ganz
   *  hinten; bei Gleichstand wird stabil alphabetisch aufgeloest. */
  private compareNks(a: string, b: string): number {
    const rank = (v: string): number => {
      const s = String(v || '').trim().toUpperCase();
      if (!s) return NKS_ORDER.length + 1;        // leer ganz nach hinten
      const i = NKS_ORDER.indexOf(s);
      return i < 0 ? NKS_ORDER.length : i;         // unbekannt hinter die bekannten
    };
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b, 'de', { sensitivity: 'base' });
  }
}
